import { useEffect, useState } from "react";
import { api, formatErr, getFileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Plus, Upload, Trash2, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";
import { SkeletonTable } from "../../components/Skeleton";

const emptyForm = {
  full_name: "", personal_email: "", phone: "", alt_phone: "", aadhaar_number: "",
  company_email: "", address: "", employee_type: "full-time",
  joining_date: "", leaving_date: "", notice_period_months: 1,
  username: "", password: "",
  aadhaar_file_path: "", photo_file_path: "",
  monthly_salary: 5000,
};

export default function Employees() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false); // false | employeeId
  const [viewData, setViewData] = useState(null);

  const load = () => api.get("/employees").then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const upload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.path }));
      toast.success("File uploaded");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const openCreate = () => { setForm(emptyForm); setEditMode(false); setOpen(true); };
  const openEdit = (emp) => {
    setForm({
      full_name: emp.full_name || "", personal_email: emp.personal_email || "",
      phone: emp.phone || "", alt_phone: emp.alt_phone || "",
      aadhaar_number: emp.aadhaar_number || "",
      company_email: emp.company_email || "", address: emp.address || "",
      employee_type: emp.employee_type || "full-time",
      joining_date: emp.joining_date || "", leaving_date: emp.leaving_date || "",
      notice_period_months: emp.notice_period_months || 0,
      username: emp.username || "", password: "",
      aadhaar_file_path: emp.aadhaar_file_path || "",
      photo_file_path: emp.photo_file_path || "",
      monthly_salary: emp.monthly_salary ?? 5000,
    });
    setEditMode(emp.employee_id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editMode) {
        const payload = { ...form, notice_period_months: Number(form.notice_period_months) || 0, monthly_salary: Number(form.monthly_salary) || 0 };
        delete payload.password;
        delete payload.username;
        await api.patch(`/employees/${editMode}`, payload);
        toast.success("Employee updated");
      } else {
        const payload = { ...form, notice_period_months: Number(form.notice_period_months) || 0, monthly_salary: Number(form.monthly_salary) || 0 };
        await api.post("/employees", payload);
        toast.success("Employee created");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditMode(false);
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this employee? This action cannot be undone.")) return;
    try { await api.delete(`/employees/${id}`); load(); toast.success("Deleted"); } catch (err) { toast.error(formatErr(err)); }
  };

  const viewEmployee = async (id) => {
    try {
      const { data } = await api.get(`/admin/employee-detail/${id}`);
      setViewData(data);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB") : "—";

  return (
    <div className="space-y-6" data-testid="admin-employees-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">01 / Roster</div>
          <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">Employees.</h1>
        </div>
        <Button className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-11 px-6" data-testid="add-employee-btn" onClick={openCreate}>
          <Plus size={14} className="mr-2" /> Add Employee
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditMode(false); setForm(emptyForm); } }}>
        <DialogContent className="max-w-3xl rounded-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="rule-text text-2xl">{editMode ? "Edit Employee" : "New Employee"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testid="emp-fullname" />
            <Field label="Personal Email" type="email" value={form.personal_email} onChange={(v) => setForm({ ...form, personal_email: v })} required testid="emp-personal-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="emp-phone" />
            <Field label="Alt Phone" value={form.alt_phone} onChange={(v) => setForm({ ...form, alt_phone: v })} testid="emp-alt-phone" />
            <Field label="Aadhaar Number" value={form.aadhaar_number} onChange={(v) => setForm({ ...form, aadhaar_number: v })} required testid="emp-aadhaar" />
            <Field label="Company Email" type="email" value={form.company_email} onChange={(v) => setForm({ ...form, company_email: v })} required testid="emp-company-email" />
            <div className="col-span-2">
              <Label className="eyebrow">Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required className="rounded-xl mt-1" data-testid="emp-address" />
            </div>
            <div>
              <Label className="eyebrow">Employee Type</Label>
              <Select value={form.employee_type} onValueChange={(v) => setForm({ ...form, employee_type: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="emp-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="intern">Intern</SelectItem>
                  <SelectItem value="part-time">Part-Time</SelectItem>
                  <SelectItem value="full-time">Full-Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Joining Date" type="date" value={form.joining_date} onChange={(v) => setForm({ ...form, joining_date: v })} required testid="emp-joining" />
            {form.employee_type === "intern" ? (
              <Field label="Leaving Date" type="date" value={form.leaving_date} onChange={(v) => setForm({ ...form, leaving_date: v })} required testid="emp-leaving" />
            ) : (
              <Field label="Notice Period (months)" type="number" value={form.notice_period_months} onChange={(v) => setForm({ ...form, notice_period_months: v })} required testid="emp-notice" />
            )}
            {!editMode && <>
              <Field label="Username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} required testid="emp-username" />
              <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testid="emp-password" />
            </>}
            <Field label="Monthly Salary (₹)" type="number" value={form.monthly_salary} onChange={(v) => setForm({ ...form, monthly_salary: v })} required testid="emp-monthly-salary" />

            <FileUpload label="Aadhaar Document" field="aadhaar_file_path" onUpload={upload} value={form.aadhaar_file_path} testid="aadhaar-upload" />
            <FileUpload label="Employee Photo" field="photo_file_path" onUpload={upload} value={form.photo_file_path} testid="photo-upload" />

            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold" data-testid="emp-submit-btn">
                {saving ? "Saving..." : (editMode ? "Save Changes" : "Create Employee")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm overflow-x-auto">
          {loading ? <div className="p-4"><SkeletonTable rows={4} cols={7} /></div> : (
          <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">ID</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Name</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Type</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Company Email</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Phone</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Username</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-500 dark:text-zinc-400 text-sm">No employees yet. Add one to get started.</TableCell></TableRow>
            ) : list.map((e) => (
              <TableRow key={e.id} data-testid={`employee-row-${e.employee_id}`}>
                <TableCell className="font-mono text-xs">{e.employee_id}</TableCell>
                <TableCell className="font-bold">{e.full_name}</TableCell>
                <TableCell><span className="eyebrow bg-zinc-100 dark:bg-zinc-800 px-2 py-1 border border-zinc-200 dark:border-zinc-800">{e.employee_type}</span></TableCell>
                <TableCell>{e.company_email}</TableCell>
                <TableCell>{e.phone}</TableCell>
                <TableCell className="font-mono text-xs">{e.username}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" className="rounded-xl h-8 w-8 p-0 hover:bg-[#4F46E5]/10 hover:text-[#4F46E5]" onClick={() => viewEmployee(e.employee_id)} data-testid={`view-${e.employee_id}`}>
                      <Eye size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-xl h-8 w-8 p-0 hover:bg-amber-50 hover:text-amber-700" onClick={() => openEdit(e)} data-testid={`edit-${e.employee_id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-xl h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600" onClick={() => remove(e.employee_id)} data-testid={`delete-${e.employee_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        )}
      </div>
      <Dialog open={!!viewData} onOpenChange={(o) => !o && setViewData(null)}>
        <DialogContent className="max-w-4xl rounded-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="rule-text text-2xl">Employee Profile</DialogTitle></DialogHeader>
          {viewData && (() => {
            const e = viewData.employee;
            const att = viewData.current_month_attendance || [];
            const pay = viewData.last_month_payment;
            const totalHours = att.reduce((s, r) => s + (r.working_hours || 0), 0);
            const fullDays = att.filter((r) => (r.working_hours || 0) >= 3).length;
            return (
              <div className="space-y-6" data-testid="employee-view-modal">
                {/* Header with photo */}
                <div className="flex items-start gap-6 border-b border-zinc-200 dark:border-zinc-800 pb-6">
                  <div className="w-28 h-28 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 overflow-hidden flex items-center justify-center">
                    {e.photo_file_path ? (
                      <img src={getFileUrl(e.photo_file_path)} alt={e.full_name} className="w-full h-full object-cover" data-testid="emp-photo" />
                    ) : <span className="eyebrow text-zinc-400 dark:text-zinc-500">No Photo</span>}
                  </div>
                  <div className="flex-1">
                    <div className="eyebrow">{e.employee_id} · {e.employee_type}</div>
                    <h3 className="rule-text text-3xl mt-1">{e.full_name}</h3>
                    <div className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{e.company_email}</div>
                  </div>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <InfoRow label="Personal Email" value={e.personal_email} />
                  <InfoRow label="Phone" value={e.phone} />
                  <InfoRow label="Alt Phone" value={e.alt_phone || "—"} />
                  <InfoRow label="Aadhaar Number" value={e.aadhaar_number} />
                  <InfoRow label="Username" value={e.username} />
                  <InfoRow label="Monthly Salary" value={`₹ ${(e.monthly_salary ?? 5000).toLocaleString("en-IN")}`} />
                  <InfoRow label="Joining Date" value={e.joining_date} />
                  {e.employee_type === "intern"
                    ? <InfoRow label="Leaving Date" value={e.leaving_date || "—"} />
                    : <InfoRow label="Notice Period" value={`${e.notice_period_months || 0} months`} />}
                  <InfoRow label="Address" value={e.address} full />
                  {e.aadhaar_file_path && <div className="col-span-2">
                    <div className="eyebrow">Aadhaar Document</div>
                    <a href={getFileUrl(e.aadhaar_file_path)} target="_blank" rel="noreferrer" className="text-[#4F46E5] text-sm hover:underline">View Document →</a>
                  </div>}
                </div>

                {/* Current month attendance */}
                <div>
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="eyebrow">Current Month Attendance · {String(viewData.current_month.month).padStart(2, "0")}/{viewData.current_month.year}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Days logged: <span className="font-bold">{att.length}</span> · Full days: <span className="font-bold">{fullDays}</span> · Total hrs: <span className="font-bold">{totalHours.toFixed(2)}</span></div>
                  </div>
                  <div className="border border-zinc-200 dark:border-zinc-800 max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Date</TableHead>
                          <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">In</TableHead>
                          <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Out</TableHead>
                          <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Hours</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {att.length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="text-center text-zinc-500 dark:text-zinc-400 py-6 text-sm">No records this month.</TableCell></TableRow>
                        ) : att.map((r) => (
                          <TableRow key={r.date}>
                            <TableCell className="font-mono">{r.date}</TableCell>
                            <TableCell className="font-mono">{fmt(r.login_time)}</TableCell>
                            <TableCell className="font-mono">{fmt(r.logout_time)}</TableCell>
                            <TableCell className="font-mono font-bold">{r.working_hours || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Last month salary */}
                <div>
                  <div className="eyebrow mb-3">Last Month Salary · {String(viewData.last_month.month).padStart(2, "0")}/{viewData.last_month.year}</div>
                  {pay ? (
                    <div className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4 grid grid-cols-4 gap-4 text-sm">
                      <div><div className="eyebrow">Base</div><div className="font-mono font-bold mt-1">₹ {pay.base_salary.toFixed(2)}</div></div>
                      <div><div className="eyebrow">Reimb + Bonus - Ded</div><div className="font-mono font-bold mt-1">₹ {(pay.reimbursement_total + pay.bonus - pay.deduction).toFixed(2)}</div></div>
                      <div><div className="eyebrow">Total</div><div className="font-mono font-bold mt-1 text-[#4F46E5]">₹ {pay.total.toFixed(2)}</div></div>
                      <div><div className="eyebrow">Status</div><div className="mt-1"><span className={`eyebrow px-2 py-1 border ${pay.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}>{pay.status}</span></div></div>
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-500 dark:text-zinc-400 italic">No salary processed for last month.</div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, required, testid }) => (
  <div>
    <Label className="eyebrow">{label}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="rounded-xl mt-1" data-testid={testid} />
  </div>
);

const FileUpload = ({ label, field, onUpload, value, testid }) => (
  <div>
    <Label className="eyebrow">{label}</Label>
    <label className="mt-1 flex items-center gap-2 border border-zinc-300 px-3 h-10 cursor-pointer hover:bg-zinc-50 text-sm">
      <Upload size={14} />
      {value ? <span className="truncate text-xs text-emerald-600">Uploaded ✓</span> : <span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Choose file...</span>}
      <input type="file" className="hidden" onChange={(e) => onUpload(e, field)} data-testid={testid} />
    </label>
  </div>
);

const InfoRow = ({ label, value, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <div className="eyebrow">{label}</div>
    <div className="font-bold mt-0.5 break-words">{value || "—"}</div>
  </div>
);
